'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UserAccount {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  phone?: string;
  createdAt: string;
  ordersCount: number;
}

interface AuthState {
  user: UserAccount | null;
  isOpen: boolean;
  mode: 'login' | 'register' | 'profile';
  registeredUsers: Array<{ email: string; password: string; user: UserAccount }>;
  login: (email: string, password: string) => { success: boolean; error?: string };
  register: (data: { firstname: string; lastname: string; email: string; password: string; phone?: string }) => { success: boolean; error?: string };
  logout: () => void;
  updateProfile: (data: Partial<UserAccount>) => void;
  openAuth: (mode?: 'login' | 'register' | 'profile') => void;
  closeAuth: () => void;
  incrementOrders: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isOpen: false,
      mode: 'login',
      registeredUsers: [],

      login: (email, password) => {
        const found = get().registeredUsers.find(
          (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
        );
        if (!found) {
          return { success: false, error: 'Email ou mot de passe incorrect.' };
        }
        set({ user: found.user, isOpen: false });
        return { success: true };
      },

      register: (data) => {
        const exists = get().registeredUsers.find(
          (u) => u.email.toLowerCase() === data.email.toLowerCase()
        );
        if (exists) {
          return { success: false, error: 'Un compte existe déjà avec cet email.' };
        }
        const newUser: UserAccount = {
          id: `user-${Date.now()}`,
          email: data.email,
          firstname: data.firstname,
          lastname: data.lastname,
          phone: data.phone,
          createdAt: new Date().toISOString(),
          ordersCount: 0,
        };
        set((state) => ({
          registeredUsers: [
            ...state.registeredUsers,
            { email: data.email, password: data.password, user: newUser },
          ],
          user: newUser,
          isOpen: false,
        }));
        return { success: true };
      },

      logout: () => set({ user: null, isOpen: false }),

      updateProfile: (data) =>
        set((state) => {
          if (!state.user) return state;
          const updatedUser = { ...state.user, ...data };
          return {
            user: updatedUser,
            registeredUsers: state.registeratedUsers?.map((u) =>
              u.email === state.user?.email ? { ...u, user: updatedUser } : u
            ) ?? state.registeredUsers,
          };
        }),

      openAuth: (mode = 'login') => set({ isOpen: true, mode }),
      closeAuth: () => set({ isOpen: false }),

      incrementOrders: () =>
        set((state) => {
          if (!state.user) return state;
          const updatedUser = { ...state.user, ordersCount: state.user.ordersCount + 1 };
          return {
            user: updatedUser,
            registeredUsers: state.registeredUsers.map((u) =>
              u.email === state.user?.email ? { ...u, user: updatedUser } : u
            ),
          };
        }),
    }),
    { name: 'matar-auth' }
  )
);
